import React, { useEffect, useState } from "react";
import {
  IResourceComponentsProps,
  BaseRecord,
  useTranslate,
  CrudFilters,
  useApiUrl,
  useCustomMutation,
} from "@refinedev/core";
import {
  useTable,
  List,
  EditButton,
  ShowButton,
  TagField,
  EmailField,
  TextField,
  DeleteButton,
  FilterDropdown,
} from "@refinedev/antd";
import {
  Table,
  Space,
  Select,
  Button,
  Avatar,
  Typography,
  Tag,
  Tooltip,
  notification,
  Segmented,
  Modal,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  EditOutlined,
  MailOutlined,
  PlusOutlined,
  SyncOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import {
  SuperadminRole,
  AdminRole,
  UserRole as UserRoleConstant,
  ServiceRole,
  TunerRole,
} from "../../models/user";
import { useAdmin, useSuperAdmin } from "../../hooks/auth";
import { getAvatarColor, getInitials } from "../../shared/utils";
import { GlassCard, ActionButton } from "../../shared/components";
import { useAvatarPalette, usePageStyles } from "../../shared/styles";
import { UserCreateModal } from "./UserCreateModal";
import { UserEditModal } from "./UserEditModal";
import {
  axiosInstance,
  startImpersonationSession,
} from "../../authProvider";

const { Text } = Typography;

interface UserListProps extends IResourceComponentsProps {
  usersData?: any[] | null;
  /** Bare table with an external create button — used inside account/tenant show pages. */
  embedded?: boolean;
  /** Keep the full page, but drop the List title/breadcrumb — used inside hub tabs. */
  hideHeader?: boolean;
  /** Limit to Admin/Tuner with a pill switcher — used in the Workflow hub. */
  staffOnly?: boolean;
  onCreateUser?: () => void;
}

export const UserList: React.FC<UserListProps> = ({
  usersData = null,
  embedded = false,
  hideHeader = false,
  staffOnly = false,
  onCreateUser,
}) => {
  const _t = useTranslate();
  const styles = usePageStyles();
  const avatarPalette = useAvatarPalette();
  const isSuperAdmin = useSuperAdmin();
  const isAdmin = useAdmin();
  const canImpersonate = isAdmin || isSuperAdmin;
  const apiUrl = useApiUrl();
  const { mutate: resendInvitation, isLoading: isResending } =
    useCustomMutation();
  const [isImpersonatingUser, setIsImpersonatingUser] = useState(false);

  const handleImpersonate = (record: BaseRecord) => {
    const displayName = [record.firstName, record.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    Modal.confirm({
      title: _t("users.impersonate.confirmTitle"),
      content: _t("users.impersonate.confirmContent", {
        name: displayName || record.email,
      }),
      okText: _t("users.impersonate.confirmOk"),
      cancelText: _t("buttons.cancel"),
      onOk: async () => {
        setIsImpersonatingUser(true);
        try {
          const response = await axiosInstance.post("/auth/impersonate", {
            userId: Number(record.id),
          });
          await startImpersonationSession(response.data);
          window.location.href = "/account-home";
        } catch {
          notification.error({
            message: _t("users.impersonate.error"),
          });
        } finally {
          setIsImpersonatingUser(false);
        }
      },
    });
  };

  const isCustomerRole = (record: BaseRecord): boolean => {
    const roleId = (record as { role?: { id?: number | string } }).role?.id;
    return (
      roleId === UserRoleConstant.id ||
      roleId === String(UserRoleConstant.id) ||
      Number(roleId) === UserRoleConstant.id
    );
  };
  const [staffRoleId, setStaffRoleId] = useState<number>(TunerRole.id);
  const { tableProps, tableQueryResult, setFilters, filters } = useTable({
    syncWithLocation: !staffOnly,
    resource: "users",
    filters: {
      mode: "server",
      permanent: staffOnly
        ? [
            {
              field: "role",
              operator: "eq",
              value: staffRoleId,
            },
          ]
        : undefined,
    },
    queryOptions: {
      enabled: true,
    },
  });
  const [users, setUsers] = useState<any[]>(usersData || []);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState<number | string | null>(
    null,
  );

  useEffect(() => {
    if (!usersData) {
      setUsers(tableQueryResult?.data?.data || []);
    } else {
      setUsers(usersData);
    }
  }, [tableQueryResult?.data?.data, usersData]);

  const staffRolePills = staffOnly ? (
    <Segmented
      value={staffRoleId}
      onChange={(value) => setStaffRoleId(value as number)}
      options={[
        {
          label: _t("users.fields.roleValues.Tuner", "Tuner"),
          value: TunerRole.id,
        },
        {
          label: _t("users.fields.roleValues.Admin", "Admin"),
          value: AdminRole.id,
        },
      ]}
    />
  ) : null;

  const tableContent = (
    <>
      <style>{`
        .users-list-table-view .ant-table {
          background: transparent !important;
        }
        .users-list-table-view .ant-table-thead > tr > th {
          background: transparent !important;
        }
        .users-list-table-view .ant-table-tbody > tr > td {
          background: transparent !important;
        }
      `}</style>
      {staffRolePills && (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          {staffRolePills}
        </div>
      )}
      <GlassCard className="users-list-table-view !p-0 overflow-hidden">
        <Table
          {...tableProps}
          pagination={embedded ? false : undefined}
          dataSource={usersData ? users : tableProps.dataSource}
          rowKey="id"
        >
          <Table.Column
          title={_t("users.fields.name", "Nom")}
          key="user"
          width={480}
          render={(_, record: any) => (
            <Space>
              <Avatar
                shape="square"
                size={44}
                src={record.photo?.path}
                style={{
                  backgroundColor: record.photo?.path
                    ? undefined
                    : getAvatarColor(record.id, avatarPalette),
                  fontWeight: 600,
                }}
              >
                {record.photo?.path
                  ? null
                  : getInitials(record.firstName, record.lastName)}
              </Avatar>
              <div>
                <Space size={4}>
                  <Text strong>
                    {record.firstName} {record.lastName}
                  </Text>
                  <Tag
                    color={
                      record.role?.name === "Superadmin"
                        ? "red"
                        : record.role?.name === "Admin"
                        ? "blue"
                        : record.role?.name === "Tuner"
                        ? "yellow"
                        : record.role?.name === "Service"
                        ? "orange"
                        : "default"
                    }
                  >
                    {_t(
                      `users.fields.roleValues.${
                        record.role?.name || "User"
                      }`,
                    )}
                  </Tag>
                </Space>
                <br />
                <Space>
                  <Text type="secondary" className="text-xs">
                    #{record.id} •
                  </Text>
                  <Text type="secondary" className="text-xs">
                    {record.status?.name?.toLowerCase() === "active" ? (
                      <>
                        <CheckCircleOutlined className="text-green-400 mr-1" />
                        {" "}
                        {_t(
                          `users.fields.statusValues.${record.status?.name?.toLowerCase()}`,
                        )}
                      </>
                    ) : record.status?.name?.toLowerCase() === "pending" ? (
                      <>
                        <SyncOutlined className="text-amber-500 mr-1" />
                        {" "}
                        {_t("users.fields.statusValues.pending")}
                      </>
                    ) : (
                      <>
                        <ClockCircleOutlined className="text-red-400 mr-1" />
                        {" "}
                        {_t(
                          `users.fields.statusValues.${record.status?.name?.toLowerCase()}`,
                        )}
                      </>
                    )}
                  </Text>
                </Space>
              </div>
            </Space>
          )}
        />
          <Table.Column
          dataIndex={["email"]}
          title={_t("users.fields.email")}
          render={(value: any) => <EmailField value={value} copyable />}
        />

          <Table.Column
          dataIndex={["role", "name"]}
          title={_t("users.fields.role")}
          render={(value: any) => (
            <TextField value={_t(`users.fields.roleValues.${value}`)} />
          )}
          filterDropdown={
            staffOnly
              ? undefined
              : (props) => {
            const currentRoleFilter = filters?.find(
              (f) => "field" in f && f.field === "role",
            );
            const currentRoleValue =
              currentRoleFilter && "value" in currentRoleFilter
                ? (currentRoleFilter.value as number)
                : undefined;

            const displayValue =
              (props.selectedKeys?.[0] as number | undefined) ||
              currentRoleValue;
            if (
              currentRoleValue !== undefined &&
              (!props.selectedKeys || props.selectedKeys.length === 0)
            ) {
              props.setSelectedKeys([currentRoleValue]);
            }

            const handleFilter = () => {
              const selectedValue = props.selectedKeys?.[0] as
                | number
                | undefined;

              const otherFilters =
                filters?.filter(
                  (f) => !("field" in f && f.field === "role"),
                ) || [];

              const newFilters: CrudFilters = [...otherFilters];

              if (selectedValue !== undefined && selectedValue !== null) {
                newFilters.push({
                  field: "role",
                  operator: "eq",
                  value: selectedValue,
                });
              }

              setFilters(newFilters);
              props.confirm();
            };

            const handleReset = () => {
              const otherFilters =
                filters?.filter(
                  (f) => !("field" in f && f.field === "role"),
                ) || [];
              setFilters(otherFilters);
              props.setSelectedKeys([]);
              props.clearFilters?.();
              props.confirm();
            };

            return (
              <div className="p-2">
                <Select
                  placeholder={_t("users.fields.role", "Role")}
                  allowClear
                  className="w-[200px] mb-2 block"
                  value={displayValue}
                  onChange={(value) => {
                    props.setSelectedKeys(value !== undefined ? [value] : []);
                  }}
                  options={(() => {
                    const allRoles = [
                      {
                        label: _t(
                          "users.fields.roleValues.Superadmin",
                          "Superadmin",
                        ),
                        value: SuperadminRole.id,
                      },
                      {
                        label: _t("users.fields.roleValues.Admin", "Admin"),
                        value: AdminRole.id,
                      },
                      {
                        label: _t("users.fields.roleValues.User", "User"),
                        value: UserRoleConstant.id,
                      },
                      {
                        label: _t("users.fields.roleValues.Tuner", "Tuner"),
                        value: TunerRole.id,
                      },
                    ];

                    if (isSuperAdmin) {
                      return allRoles;
                    } else {
                      return allRoles.filter(
                        (role) => role.value !== SuperadminRole.id,
                      );
                    }
                  })()}
                />
                <Space className="mt-2">
                  <Button
                    type="primary"
                    onClick={handleFilter}
                    size="small"
                    className="w-[90px]"
                  >
                    {_t("buttons.filter", "Filter")}
                  </Button>
                  <Button
                    onClick={handleReset}
                    size="small"
                    className="w-[90px]"
                  >
                    {_t("buttons.clear", "Clear")}
                  </Button>
                </Space>
              </div>
            );
          }
          }
          filteredValue={
            staffOnly
              ? null
              : (() => {
            const currentRoleFilter = filters?.find(
              (f) => "field" in f && f.field === "role",
            );
            return currentRoleFilter && "value" in currentRoleFilter
              ? [currentRoleFilter.value as number]
              : null;
          })()
          }
        />
        <Table.Column
          title={_t("table.actions")}
          dataIndex="actions"
          render={(_, record: BaseRecord & { status?: { name?: string } }) => {
            const isPending =
              record.status?.name?.toLowerCase() === "pending";
            return (
              <Space>
                {isPending ? (
                  <>
                    <Tooltip title={_t("users.actions.resendInvitation")}>
                      <Button
                        icon={<MailOutlined />}
                        size="small"
                        loading={isResending}
                        aria-label={_t("users.actions.resendInvitation")}
                        onClick={() => {
                          resendInvitation(
                            {
                              url: `${apiUrl}/users/${record.id}/resend-invitation`,
                              method: "post",
                              values: {},
                            },
                            {
                              onSuccess: () => {
                                notification.success({
                                  message: _t(
                                    "users.actions.resendInvitationSuccess",
                                  ),
                                });
                              },
                              onError: () => {
                                notification.error({
                                  message: _t(
                                    "users.actions.resendInvitationError",
                                  ),
                                });
                              },
                            },
                          );
                        }}
                      />
                    </Tooltip>
                    <DeleteButton
                      resource="users"
                      hideText
                      size="small"
                      recordItemId={record.id}
                      confirmTitle={_t("users.actions.cancelInvitationConfirm")}
                      mutationOptions={{
                        onSuccess: () => {
                          if (usersData) {
                            setUsers((prev) =>
                              prev.filter((u) => u.id !== record.id),
                            );
                          }
                        },
                      }}
                    />
                  </>
                ) : (
                  <>
                    <Button
                      icon={<EditOutlined />}
                      size="small"
                      onClick={() => {
                        setEditingUserId(record.id as number | string);
                        setEditModalOpen(true);
                      }}
                    />
                    {canImpersonate && isCustomerRole(record) && (
                      <Tooltip title={_t("users.impersonate.action")}>
                        <Button
                          icon={<UserSwitchOutlined />}
                          size="small"
                          loading={isImpersonatingUser}
                          aria-label={_t("users.impersonate.action")}
                          onClick={() => handleImpersonate(record)}
                        />
                      </Tooltip>
                    )}
                    <DeleteButton
                      resource="users"
                      hideText
                      size="small"
                      recordItemId={record.id}
                    />
                  </>
                )}
              </Space>
            );
          }}
          />
        </Table>
      </GlassCard>
    </>
  );

  if (embedded) {
    return (
      <div>
        <div className="flex justify-end mb-6">
          {onCreateUser && (
            <ActionButton variant="primary" onClick={onCreateUser}>
              {_t("users.titles.invite")}
            </ActionButton>
          )}
        </div>
        {tableContent}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <List
        resource="users"
        title={hideHeader ? false : undefined}
        breadcrumb={hideHeader ? false : undefined}
        headerButtons={({ createButtonProps: _ }) => [
          <Button
            key="create"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalOpen(true)}
          >
            {_t("users.titles.create")}
          </Button>,
        ]}
      >
        {tableContent}
        <UserCreateModal
          open={createModalOpen}
          onCancel={() => setCreateModalOpen(false)}
          onSuccess={() => {
            setCreateModalOpen(false);
            tableQueryResult?.refetch();
          }}
        />
        <UserEditModal
          open={editModalOpen}
          userId={editingUserId}
          onCancel={() => {
            setEditModalOpen(false);
            setEditingUserId(null);
          }}
          onSuccess={() => {
            setEditModalOpen(false);
            setEditingUserId(null);
            tableQueryResult?.refetch();
          }}
        />
      </List>
    </div>
  );
};
